import React, { useEffect, useState } from "react";

const STORAGE_KEY = "rfg_install_dismissed";

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null); // Android Chrome event
  const [showIOSHint, setShowIOSHint] = useState(false);
  const [dismissed, setDismissed] = useState(true); // start hidden; show only after we know context

  useEffect(() => {
    if (isStandalone()) return; // already installed
    try {
      if (localStorage.getItem(STORAGE_KEY) === "1") return;
    } catch (e) {
      console.debug("install prompt storage check skipped:", e?.message);
    }
    setDismissed(false);

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // iOS Safari has no beforeinstallprompt event — show hint after a short delay
    let iosTimer = null;
    if (isIOS()) {
      iosTimer = setTimeout(() => setShowIOSHint(true), 1800);
    }

    const onInstalled = () => {
      setDeferred(null);
      setShowIOSHint(false);
      setDismissed(true);
    };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (e) {
      console.debug("install dismiss storage skipped:", e?.message);
    }
    setDismissed(true);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const choice = await deferred.userChoice.catch(() => ({ outcome: "dismissed" }));
    setDeferred(null);
    if (choice.outcome === "accepted") dismiss();
  };

  if (dismissed) return null;

  // Android (or any browser exposing beforeinstallprompt)
  if (deferred) {
    return (
      <div
        className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-30 rounded-2xl border border-[#d8d3c4] bg-[#fbf8ec] shadow-lg p-4 sm:p-5 flex items-start gap-3"
        data-testid="install-prompt"
      >
        <div
          className="font-serif text-2xl text-[#B85C38] mt-0.5 select-none"
          aria-hidden="true"
        >
          ⟡
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6C5943] mb-1">
            keep the grove close
          </div>
          <div className="text-sm text-[#1C241F] mb-3 leading-snug">
            Add Root Flame Grove to your home screen so the kin are one tap away.
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={install}
              className="text-[10px] tracking-[0.28em] uppercase px-4 py-2 rounded-full bg-[#B85C38] text-[#F4F1E8] hover:bg-[#a04d2c] transition-colors"
              data-testid="install-accept"
            >
              install
            </button>
            <button
              onClick={dismiss}
              className="text-[10px] tracking-[0.28em] uppercase text-[#526357] hover:text-[#1C241F] transition-colors"
              data-testid="install-dismiss"
            >
              not now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // iOS Safari — manual hint
  if (showIOSHint) {
    return (
      <div
        className="fixed bottom-20 left-4 right-4 z-30 rounded-2xl border border-[#d8d3c4] bg-[#fbf8ec] shadow-lg p-4 flex items-start gap-3"
        data-testid="install-prompt-ios"
      >
        <div
          className="font-serif text-2xl text-[#B85C38] mt-0.5 select-none"
          aria-hidden="true"
        >
          ⟡
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6C5943] mb-1">
            add to home screen
          </div>
          <div className="text-sm text-[#1C241F] leading-snug">
            Tap the{" "}
            <span aria-label="share" className="inline-flex items-center justify-center w-5 h-5 rounded border border-[#526357] text-[#526357] align-middle mx-0.5">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 3v12" />
                <polyline points="7 8 12 3 17 8" />
                <path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
              </svg>
            </span>{" "}
            share icon below, then choose{" "}
            <strong className="text-[#B85C38]">Add to Home Screen</strong>.
          </div>
          <button
            onClick={dismiss}
            className="mt-2 text-[10px] tracking-[0.28em] uppercase text-[#526357] hover:text-[#1C241F] transition-colors"
            data-testid="install-dismiss-ios"
          >
            got it
          </button>
        </div>
      </div>
    );
  }

  return null;
}
